import api from '../../api/client';
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Table,
  Drawer,
  Modal,
  Form,
  Button,
  Select,
  DatePicker,
  Input,
  InputNumber,
  Upload,
  Badge,
  Space,
  message,
  Card,
  Row,
  Col,
  Typography,
  Divider,
  Tag,
  Descriptions,
  Alert,
  Statistic,
  Spin,
  Result,
} from "antd";
import {
  PlusOutlined,
  UploadOutlined,
  ReloadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  CheckOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import config from "../../config";
import { useSelector } from "react-redux";
import { formatKES } from "../../utils/currency";
import { saveAs } from "file-saver";
import { exportReceipts } from "../../api";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

export default function PaymentsPage() {
  const actionRef = useRef();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [filters, setFilters] = useState({
    source_type: "",
    status: "",
    paymentMethod: "",
  });
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [modalState, setModalState] = useState({
    create: false,
    reconcile: false,
    statement: false,
  });

  const environment =
    window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;
  const account = useSelector((state) => state.account.user);
  const userId = account.id;
  const userName = account.firstname;
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch available statuses from DB
  const fetchStatuses = useCallback(async () => {
    try {
      const { data } = await api.get(`/payment-statuses`);
      setStatusOptions(data);
    } catch (error) {
      console.error("Failed to fetch statuses", error);
      // Fallback to hardcoded if endpoint missing
      setStatusOptions([
        { value: "", label: "All" },
        { value: "pending", label: "Pending" },
        { value: "completed", label: "Completed" },
        { value: "reconciled", label: "Reconciled" },
        { value: "failed", label: "Failed" },
      ]);
    }
  }, [apiUrl]);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams(filters).toString();
      const { data } = await api.get(`/get-payments?${query}`);
setPayments(data);
    } catch (error) {
      message.error("Failed to fetch payments");
    } finally {
      setLoading(false);
    }
  }, [filters, apiUrl]);

  const fetchBankAccounts = useCallback(async () => {
    try {
      const { data } = await api.get(`/payments/bank-accounts`);
      setBankAccounts(data);
    } catch (error) {
      console.error("Error fetching bank accounts", error);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchStatuses();
    fetchBankAccounts();
  }, [fetchStatuses, fetchBankAccounts]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const openDrawer = (record) => {
    setSelectedPayment(record);
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setSelectedPayment(null);
  };

  const openModal = (modalName, payment = null) => {
    if (modalName === "reconcile" && payment) {
      setSelectedPayment(payment);
    }
    setModalState((prev) => ({ ...prev, [modalName]: true }));
  };

  const closeModal = (modalName) => {
    setModalState((prev) => ({ ...prev, [modalName]: false }));
    if (modalName === "reconcile") setSelectedPayment(null);
  };

  // Client-side search across description, reference, amount, method
  const filteredPayments = payments.filter((payment) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      payment.note?.toLowerCase().includes(term) ||
      `${payment.source_type}-${payment.source_id}`
        .toLowerCase()
        .includes(term) ||
      payment.amount?.toString().includes(term) ||
      payment.paymentMethod?.toLowerCase().includes(term) ||
      payment.status?.toLowerCase().includes(term)
    );
  });

  const exportToCSV = () => {
    const headers = [
      "ID",
      "Date",
      "Description",
      "Type",
      "Amount (KES)",
      "Method",
      "Status",
      "Debit Account",
      "Credit Account",
      "Reference",
    ];
    const rows = filteredPayments.map((p) => [
      p.id,
      dayjs(p.paymentDate).format("YYYY-MM-DD"),
      p.note,
      p.source_type,
      p.amount,
      p.paymentMethod,
      p.status,
      p.debit_name || "",
      p.credit_name || "",
      `${p.source_type}-${p.source_id}`,
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `payments_export_${dayjs().format("YYYYMMDD_HHmmss")}.csv`);
  };

  const handleDownloadReceipt = async (record) => {
    await exportReceipts({ id: record.id, format: "pdf" });
  };

  // Table columns
  const columns = [
    {
      title: "Date",
      dataIndex: "paymentDate",
      key: "date",
      render: (date) => dayjs(date).format("YYYY-MM-DD"),
      sorter: (a, b) =>
        dayjs(a.paymentDate).unix() - dayjs(b.paymentDate).unix(),
    },
    {
      title: "TRXN ID",
      dataIndex: "transactionId",
      key: "transactionId",
      sorter: (a, b) => a.transactionId.localeCompare(b.transactionId),
    },
    {
      title: "Type",
      dataIndex: "source_type",
      key: "type",
      render: (type) => type?.charAt(0).toUpperCase() + type?.slice(1),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => formatKES(amount),
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: "Method",
      dataIndex: "paymentMethod",
      key: "method",
      render: (method) => method?.charAt(0).toUpperCase() + method?.slice(1),
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let color = "default";
        if (status === "reconciled") color = "success";
        else if (status === "completed") color = "blue";
        else if (status === "pending") color = "warning";
        else if (status === "failed") color = "error";
        return <Badge color={color} text={status?.toUpperCase()} />;
      },
      filters: statusOptions.map((s) => ({ text: s.label, value: s.value })),
      onFilter: (value, record) => record.status === value,
    },

    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 100,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => openDrawer(record)}
          >
            {/* Details */}
          </Button>
          <Button
            icon={<DownloadOutlined />}
            size="small"
            onClick={() => handleDownloadReceipt(record)}
          >
            {/* Receipt */}
          </Button>
          {record.status !== "reconciled" && (
            <Button
              size="small"
              icon={<CheckOutlined />}
              onClick={() => openModal("reconcile", record)}
            >
              {/* Reconcile */}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const totalCounts = filteredPayments.length;
  const totalAmount = filteredPayments.reduce((sum, p) => sum + p.amount, 0);

  return (
    <>
      <Card
        title="Payments Management"
        extra={
          <Space>
            <Select
              style={{ width: 150 }}
              allowClear
              placeholder="All Payments"
              value={filters.source_type}
              onChange={(val) => handleFilterChange("source_type", val)}
            >
              <Option value="">All</Option>
              <Option value="transaction">Transactions</Option>
              <Option value="manual">Manual</Option>
              <Option value="salary">Salary</Option>
              <Option value="vendor">Vendor</Option>
              <Option value="tax">Tax</Option>
              <Option value="expense">Expense</Option>
            </Select>
            <Select
              style={{ width: 150 }}
              allowClear
              placeholder="All Statuses"
              value={filters.status}
              onChange={(val) => handleFilterChange("status", val)}
            >
              {statusOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
            <Select
              style={{ width: 150 }}
              allowClear
              placeholder="All Methods"
              value={filters.paymentMethod}
              onChange={(val) => handleFilterChange("paymentMethod", val)}
            >
              <Option value="">All</Option>
              <Option value="bank_transfer">Bank Transfer</Option>
              <Option value="mpesa">M-Pesa</Option>
              <Option value="cheque">Cheque</Option>
              <Option value="cash">Cash</Option>
            </Select>
            <Input.Search
              placeholder="Search by description, ref, amount..."
              allowClear
              style={{ width: 200 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onSearch={(val) => setSearchTerm(val)}
            />
            <Button icon={<DownloadOutlined />} onClick={exportToCSV}>
              Export CSV
            </Button>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => openModal("statement")}
            >
              Upload Statement
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchPayments}>
              Refresh
            </Button>
          </Space>
        }
      >
        <Row style={{ marginBottom: 16 }}>
          <Col span={12}>
            <Statistic title="Total Payments" value={totalCounts} />
          </Col>
          <Col span={12}>
            <Statistic
              title="Total Amount (KES)"
              value={formatKES(totalAmount)}
            />
          </Col>
        </Row>
        <Divider />

        <Table
          size="small"
          width="100%"
          scroll={{ x: "max-content" }}
          columns={columns}
          dataSource={filteredPayments}
          actionRef={actionRef}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
          }}
          // onRow={(record) => ({
          //   onClick: () => openDrawer(record),
          //   style: { cursor: "pointer" },
          // })}
        />
      </Card>

      {/* Details Drawer */}
      <Drawer
        title="Payment Details"
        placement="right"
        width={600}
        onClose={closeDrawer}
        open={drawerVisible}
      >
        {selectedPayment && (
          <>
            <Descriptions column={1} bordered>
              <Descriptions.Item label="ID">
                {selectedPayment.id}
              </Descriptions.Item>
              <Descriptions.Item label="Date">
                {dayjs(selectedPayment.paymentDate).format("YYYY-MM-DD HH:mm")}
              </Descriptions.Item>
              <Descriptions.Item label="Amount">
                KES {selectedPayment.amount?.toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                {selectedPayment.source_type}
              </Descriptions.Item>
              <Descriptions.Item label="Method">
                {selectedPayment.paymentMethod}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Badge
                  color={
                    selectedPayment.status === "reconciled"
                      ? "green"
                      : selectedPayment.status === "completed"
                      ? "blue"
                      : "orange"
                  }
                  text={selectedPayment.status?.toUpperCase()}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Description">
                {selectedPayment.note}
              </Descriptions.Item>
              <Descriptions.Item label="Debit Account">
                {selectedPayment.debit_name} ({selectedPayment.gl_debit})
              </Descriptions.Item>
              <Descriptions.Item label="Credit Account">
                {selectedPayment.credit_name} ({selectedPayment.gl_credit})
              </Descriptions.Item>
              <Descriptions.Item label="Created By">
                {selectedPayment.userName} (ID: {selectedPayment.userId})
              </Descriptions.Item>
              <Descriptions.Item label="Stakeholder">
                {selectedPayment.stakeholderName}
              </Descriptions.Item>
              <Descriptions.Item label="Receipt">
                {selectedPayment.receipt_url ? (
                  <a
                    href={selectedPayment.receipt_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View Receipt
                  </a>
                ) : (
                  "No receipt uploaded"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Accounts">
                {selectedPayment.debit_name} (Debit) →{" "}
                {selectedPayment.credit_name} (Credit)
              </Descriptions.Item>
            </Descriptions>
            <Divider />
            <Title level={5}>Reconciliation Info</Title>
            {selectedPayment.reconciled_at ? (
              <>
                <Text>
                  Reconciled on:{" "}
                  {dayjs(selectedPayment.reconciled_at).format(
                    "YYYY-MM-DD HH:mm",
                  )}
                </Text>
                <br />
                <Text>
                  Bank Transaction Ref: {selectedPayment.bank_txn_ref || "N/A"}
                </Text>
              </>
            ) : (
              <Text type="secondary">Not reconciled yet</Text>
            )}
          </>
        )}
      </Drawer>

      {/* Create Payment Modal (Ant Design version) */}
      <CreatePaymentModal
        visible={modalState.create}
        onCancel={() => closeModal("create")}
        onSuccess={() => {
          closeModal("create");
          fetchPayments();
        }}
        bankAccounts={bankAccounts}
        apiUrl={apiUrl}
        userId={userId}
        userName={userName}
      />

      {/* Reconcile Payment Modal */}
      <ReconcilePaymentModal
        visible={modalState.reconcile}
        onCancel={() => closeModal("reconcile")}
        payment={selectedPayment}
        onSuccess={() => {
          closeModal("reconcile");
          fetchPayments();
        }}
        apiUrl={apiUrl}
        bankAccounts={bankAccounts}
      />

      {/* Upload Statement Modal */}
      <UploadStatementModal
        visible={modalState.statement}
        onCancel={() => closeModal("statement")}
        onSuccess={() => {
          closeModal("statement");
          message.success("Statement processed");
        }}
        bankAccounts={bankAccounts}
        apiUrl={apiUrl}
      />
    </>
  );
}

// -------------------- Sub-components (Ant Design) --------------------

const CreatePaymentModal = ({
  visible,
  onCancel,
  onSuccess,
  bankAccounts,
  apiUrl,
  userId,
  userName,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [fileList, setFileList] = useState([]);

  const paymentTypeOptions = ["salary", "vendor", "tax", "expense"];
  const paymentMethodOptions = ["bank", "mpesa", "cheque", "cash"];

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const formData = new FormData();
      formData.append("source_type", values.source_type);
      formData.append("amount", values.amount);
      formData.append("paymentDate", values.paymentDate.format("YYYY-MM-DD"));
      formData.append("paymentMethod", values.paymentMethod);
      formData.append("note", values.note);
      formData.append("status", "pending");
      formData.append("gl_debit", values.gl_debit);
      formData.append("gl_credit", values.gl_credit);
      formData.append("userId", userId);
      formData.append("userName", userName);
      formData.append("paidBy", userName);
      if (fileList.length > 0) {
        formData.append("receipt", fileList[0].originFileObj);
      }

      await api.post(`/create-payments`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      message.success("Payment created");
      onSuccess();
    } catch (error) {
      message.error(error.response?.data?.error || "Creation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const bankOptions = bankAccounts.map((acc) => ({
    label: `${acc.name} (${acc.code})`,
    value: acc.code,
  }));

  return (
    <Modal
      title="Create New Payment"
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={submitting}
          onClick={handleSubmit}
        >
          Create Payment
        </Button>,
      ]}
      width={700}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ paymentDate: dayjs() }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="source_type"
              label="Payment Type"
              rules={[{ required: true }]}
            >
              <Select placeholder="Select type">
                {paymentTypeOptions.map((opt) => (
                  <Option key={opt} value={opt}>
                    {opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="amount"
              label="Amount (KES)"
              rules={[{ required: true }]}
            >
              <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="paymentDate"
              label="Payment Date"
              rules={[{ required: true }]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="paymentMethod"
              label="Payment Method"
              rules={[{ required: true }]}
            >
              <Select>
                {paymentMethodOptions.map((opt) => (
                  <Option key={opt} value={opt}>
                    {opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="gl_debit"
              label="Debit Account"
              rules={[{ required: true }]}
            >
              <Select options={bankOptions} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="gl_credit"
              label="Credit Account"
              rules={[{ required: true }]}
            >
              <Select options={bankOptions} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="note"
              label="Description"
              rules={[{ required: true }]}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Receipt/Proof">
              <Upload
                fileList={fileList}
                onChange={({ fileList }) => setFileList(fileList)}
                beforeUpload={() => false}
                maxCount={1}
                accept=".pdf,.jpg,.png"
              >
                <Button icon={<UploadOutlined />}>Upload File</Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

const ReconcilePaymentModal = ({
  visible,
  onCancel,
  payment,
  onSuccess,
  apiUrl,
  bankAccounts,
}) => {
  const [selectedBank, setSelectedBank] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [loadingTxns, setLoadingTxns] = useState(false);

  const bankOptions = bankAccounts.map((acc) => ({
    label: `${acc.name} (${acc.code})`,
    value: acc.id,
  }));

  useEffect(() => {
    if (selectedBank && payment) {
      setLoadingTxns(true);
      axios
        .get(`/reconciliation/transactions`, {
          params: {
            bankAccountId: selectedBank,
            amount: payment.amount,
            date: payment.paymentDate,
          },
        })
        .then((res) => setTransactions(res.data))
        .catch(() => message.error("Failed to fetch bank transactions"))
        .finally(() => setLoadingTxns(false));
    }
  }, [selectedBank, payment, apiUrl]);

  const handleReconcile = async () => {
    if (!selectedTxn) {
      message.warning("Select a bank transaction");
      return;
    }
    setProcessing(true);
    try {
      await api.post(`/payments/${payment.id}/reconcile`, {
        statementTransactionId: selectedTxn,
      });
      message.success("Payment reconciled");
      onSuccess();
    } catch (error) {
      message.error(error.response?.data?.message || "Reconciliation failed");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Modal
      title="Reconcile Payment"
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={processing}
          onClick={handleReconcile}
          disabled={!selectedTxn}
        >
          Reconcile
        </Button>,
      ]}
      width={800}
    >
      {payment && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Text strong>Amount:</Text> KES {payment.amount?.toLocaleString()}
            </Col>
            <Col span={8}>
              <Text strong>Date:</Text>{" "}
              {dayjs(payment.paymentDate).format("YYYY-MM-DD")}
            </Col>
            <Col span={8}>
              <Text strong>Description:</Text> {payment.note}
            </Col>
          </Row>
        </Card>
      )}
      <Form.Item label="Select Bank Account">
        <Select
          options={bankOptions}
          placeholder="Choose bank account"
          onChange={setSelectedBank}
          style={{ width: "100%" }}
        />
      </Form.Item>
      <Title level={5}>Matching Bank Transactions</Title>
      {loadingTxns ? (
        <div style={{ textAlign: "center", padding: 20 }}>Loading...</div>
      ) : transactions.length === 0 ? (
        <Text type="secondary">No matching transactions found</Text>
      ) : (
        <Table
          dataSource={transactions}
          rowKey="id"
          pagination={false}
          size="small"
          rowSelection={{
            type: "radio",
            selectedRowKeys: selectedTxn ? [selectedTxn] : [],
            onChange: (selectedRowKeys) => setSelectedTxn(selectedRowKeys[0]),
          }}
          columns={[
            {
              title: "Date",
              dataIndex: "transaction_date",
              render: (d) => dayjs(d).format("YYYY-MM-DD"),
            },
            { title: "Description", dataIndex: "description" },
            { title: "Reference", dataIndex: "reference" },
            {
              title: "Amount",
              dataIndex: "amount",
              render: (amt) => (
                <span style={{ color: amt > 0 ? "green" : "red" }}>
                  {amt?.toLocaleString()}
                </span>
              ),
            },
            {
              title: "Balance",
              dataIndex: "balance",
              render: (bal) => bal?.toLocaleString(),
            },
          ]}
        />
      )}
    </Modal>
  );
};

// const UploadStatementModal = ({
//   visible,
//   onCancel,
//   onSuccess,
//   bankAccounts,
//   apiUrl,
// }) => {
//   const [form] = Form.useForm();
//   const [uploading, setUploading] = useState(false);
//   const [fileList, setFileList] = useState([]);

//   const bankOptions = bankAccounts.map((acc) => ({
//     label: `${acc.name} (${acc.code})`,
//     value: acc.id,
//   }));

//   const handleUpload = async () => {
//     try {
//       const values = await form.validateFields();
//       if (fileList.length === 0) {
//         message.warning("Please select a file");
//         return;
//       }
//       setUploading(true);
//       const formData = new FormData();
//       formData.append("bank_account", values.bank_account);
//       formData.append(
//         "statement_date",
//         values.statement_date.format("YYYY-MM-DD"),
//       );
//       formData.append("statement", fileList[0].originFileObj);

//       const { data } = await api.post(
//         `/payments/process-statement`,
//         formData,
//         {
//           headers: { "Content-Type": "multipart/form-data" },
//         },
//       );
//       message.success(`Processed ${data.transactionCount} transactions`);
//       onSuccess();
//     } catch (error) {
//       message.error(error.response?.data?.message || "Upload failed");
//     } finally {
//       setUploading(false);
//     }
//   };

//   return (
//     <Modal
//       title="Upload Bank Statement"
//       open={visible}
//       onCancel={onCancel}
//       footer={[
//         <Button key="cancel" onClick={onCancel}>
//           Cancel
//         </Button>,
//         <Button
//           key="submit"
//           type="primary"
//           loading={uploading}
//           onClick={handleUpload}
//         >
//           Upload & Process
//         </Button>,
//       ]}
//     >
//       <Form
//         form={form}
//         layout="vertical"
//         initialValues={{ statement_date: dayjs() }}
//       >
//         <Form.Item
//           name="bank_account"
//           label="Bank Account"
//           rules={[{ required: true }]}
//         >
//           <Select options={bankOptions} placeholder="Select account" />
//         </Form.Item>
//         <Form.Item
//           name="statement_date"
//           label="Statement Date"
//           rules={[{ required: true }]}
//         >
//           <DatePicker style={{ width: "100%" }} />
//         </Form.Item>
//         <Form.Item label="Statement File" required>
//           <Upload
//             fileList={fileList}
//             onChange={({ fileList }) => setFileList(fileList)}
//             beforeUpload={() => false}
//             maxCount={1}
//             accept=".pdf,.csv"
//           >
//             <Button icon={<UploadOutlined />}>Select File</Button>
//           </Upload>
//         </Form.Item>
//         <Alert
//           message="Note"
//           description="PDF statements will be processed automatically, but may require manual verification."
//           type="info"
//           showIcon
//         />
//       </Form>
//     </Modal>
//   );
// };

const fmtAmount = (amt) => {
  if (amt === null || amt === undefined) return "—";
  const abs = Math.abs(amt).toLocaleString("en-KE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return amt < 0 ? (
    <Text type="danger">-{abs}</Text>
  ) : (
    <Text style={{ color: "#16a34a" }}>+{abs}</Text>
  );
};

const txnColumns = [
  {
    title: "Date",
    dataIndex: "transaction_date",
    width: 110,
    render: (d) => dayjs(d).format("DD MMM YYYY"),
  },
  {
    title: "Description",
    dataIndex: "description",
    ellipsis: true,
  },
  {
    title: "Reference",
    dataIndex: "reference",
    width: 140,
    render: (r) => r || <Text type="secondary">—</Text>,
  },
  {
    title: "Amount",
    dataIndex: "amount",
    width: 130,
    align: "right",
    render: fmtAmount,
  },
  {
    title: "Balance",
    dataIndex: "balance",
    width: 130,
    align: "right",
    render: (b) =>
      b !== null && b !== undefined ? (
        b.toLocaleString()
      ) : (
        <Text type="secondary">—</Text>
      ),
  },
];

// ─── component ──────────────────────────────────────────────────────────────

const UploadStatementModal = ({
  visible,
  onCancel,
  onSuccess,
  bankAccounts,
  apiUrl,
}) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);

  // stages: "idle" | "uploading" | "preview" | "saving" | "done" | "error"
  const [stage, setStage] = useState("idle");
  const [parseResult, setParseResult] = useState(null); // { transactions, transactionCount }
  const [errorMsg, setErrorMsg] = useState("");

  const bankOptions = bankAccounts.map((acc) => ({
    label: `${acc.name} (${acc.code})`,
    value: acc.id,
  }));

  const selectedFile = fileList[0] ?? null;
  const isPDF =
    selectedFile &&
    (selectedFile.name?.toLowerCase().endsWith(".pdf") ||
      selectedFile.type?.includes("pdf"));

  // ── reset when closed ────────────────────────────────────────────────────
  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    setStage("idle");
    setParseResult(null);
    setErrorMsg("");
    onCancel();
  };

  // ── step 1: parse & preview ──────────────────────────────────────────────
  const handleParseAndPreview = async () => {
    try {
      const values = await form.validateFields();
      if (!selectedFile) {
        return;
      }

      setStage("uploading");
      setErrorMsg("");

      const formData = new FormData();
      formData.append("bank_account", values.bank_account);
      formData.append(
        "statement_date",
        values.statement_date.format("YYYY-MM-DD"),
      );
      formData.append("statement", selectedFile.originFileObj ?? selectedFile);
      // Tell the backend to parse but not yet persist (if your backend supports ?preview=1)
      // If not, this endpoint both parses AND saves — see note in footer.

      const { data } = await api.post(
        `/payments/process-statement`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      setParseResult(data);
      setStage("preview");
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Upload failed";
      setErrorMsg(msg);
      setStage("error");
    }
  };

  // ── step 2: confirm save (only needed if backend has a separate confirm endpoint) ──
  // If your backend already saves on /process-statement, this step just closes:
  const handleConfirm = () => {
    setStage("done");
    onSuccess(parseResult);
  };

  // ── footer buttons by stage ──────────────────────────────────────────────
  const footerButtons = () => {
    if (stage === "idle" || stage === "uploading") {
      return [
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="parse"
          type="primary"
          loading={stage === "uploading"}
          onClick={handleParseAndPreview}
          disabled={!selectedFile}
          icon={<UploadOutlined />}
        >
          {stage === "uploading" ? "Processing…" : "Upload & Parse"}
        </Button>,
      ];
    }

    if (stage === "preview") {
      return [
        <Button key="back" onClick={() => setStage("idle")}>
          ← Change File
        </Button>,
        <Button
          key="confirm"
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={handleConfirm}
        >
          Confirm & Save ({parseResult?.transactionCount} transactions)
        </Button>,
      ];
    }

    if (stage === "error") {
      return [
        <Button key="back" onClick={() => setStage("idle")}>
          ← Try Again
        </Button>,
        <Button key="close" onClick={handleCancel}>
          Close
        </Button>,
      ];
    }

    if (stage === "done") {
      return [
        <Button key="close" type="primary" onClick={handleCancel}>
          Done
        </Button>,
      ];
    }

    return [];
  };

  // ── render ───────────────────────────────────────────────────────────────
  return (
    <Modal
      title={
        <Space>
          <FileTextOutlined />
          Upload Bank Statement
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      footer={footerButtons()}
      width={900}
      destroyOnClose
    >
      {/* ── STAGE: idle / form ── */}
      {(stage === "idle" || stage === "uploading") && (
        <Spin spinning={stage === "uploading"} tip="Parsing statement…">
          <Form
            form={form}
            layout="vertical"
            initialValues={{ statement_date: dayjs() }}
          >
            <Form.Item
              name="bank_account"
              label="Bank Account"
              rules={[{ required: true, message: "Select a bank account" }]}
            >
              <Select options={bankOptions} placeholder="Select account" />
            </Form.Item>

            <Form.Item
              name="statement_date"
              label="Statement Date"
              rules={[
                { required: true, message: "Provide the statement date" },
              ]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
              label="Statement File"
              required
              validateStatus={
                stage === "idle" && fileList.length === 0 ? "" : ""
              }
            >
              <Upload
                fileList={fileList}
                onChange={({ fileList: fl }) => setFileList(fl)}
                beforeUpload={() => false}
                maxCount={1}
                accept=".pdf,.csv"
              >
                <Button
                  icon={isPDF ? <FilePdfOutlined /> : <FileTextOutlined />}
                >
                  {selectedFile ? selectedFile.name : "Select .csv or .pdf"}
                </Button>
              </Upload>
            </Form.Item>

            {isPDF && (
              <Alert
                type="warning"
                showIcon
                icon={<ExclamationCircleOutlined />}
                message="PDF statements"
                description="PDF parsing works best with text-based (digital) statements. Scanned/image PDFs may not parse correctly — use a CSV export from your bank if available."
                style={{ marginBottom: 8 }}
              />
            )}

            <Alert
              type="info"
              showIcon
              message="Supported formats"
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  <li>
                    <strong>CSV</strong> — any delimiter; auto-detects date,
                    amount, debit/credit, balance columns
                  </li>
                  <li>
                    <strong>PDF</strong> — text-based bank statements (KCB,
                    Equity, Co-op, NCBA, ABSA, etc.)
                  </li>
                </ul>
              }
            />
          </Form>
        </Spin>
      )}

      {/* ── STAGE: preview ── */}
      {stage === "preview" && parseResult && (
        <>
          <Space style={{ marginBottom: 12 }}>
            <CheckCircleOutlined style={{ color: "#16a34a", fontSize: 18 }} />
            <Title level={5} style={{ margin: 0 }}>
              {parseResult.transactionCount} transactions parsed successfully
            </Title>
            <Tag color="green">Ready to save</Tag>
          </Space>
          <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
            Review the transactions below, then click{" "}
            <strong>Confirm & Save</strong> to post them to the reconciliation
            ledger.
          </Text>
          <Table
            dataSource={parseResult.transactions}
            columns={txnColumns}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 10, showSizeChanger: false }}
            scroll={{ x: 680 }}
            summary={(rows) => {
              const total = rows.reduce((s, r) => s + (r.amount || 0), 0);
              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <Text strong>Net Total</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    {fmtAmount(total)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} />
                </Table.Summary.Row>
              );
            }}
          />
        </>
      )}

      {/* ── STAGE: error ── */}
      {stage === "error" && (
        <Result
          status="error"
          title="Statement could not be parsed"
          subTitle={errorMsg}
          extra={
            <Alert
              type="info"
              showIcon
              message="Tips"
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  <li>Make sure the file is not password-protected.</li>
                  <li>
                    For PDFs, try exporting a CSV from your internet banking
                    instead.
                  </li>
                  <li>
                    Ensure the CSV has column headers (Date, Description,
                    Amount, etc.).
                  </li>
                </ul>
              }
            />
          }
        />
      )}

      {/* ── STAGE: done ── */}
      {stage === "done" && (
        <Result
          status="success"
          title={`${parseResult?.transactionCount || 0} transactions saved`}
          subTitle="The bank statement has been processed and is ready for reconciliation."
        />
      )}
    </Modal>
  );
};

// export default UploadStatementModal;
